import React, { useState, useEffect } from 'react'
import { CutToSizeGroupModel, CutToSizeItems, CutToSizeLogicModel } from './types/models'
import DropDown from './DropDown'
import SizeInput from './SizeInput'

import './assets/style.scss'
import CutToSizeCrossSellSummary from './CutToSizeCrossSellSummary'
import CutToSizeLogic from './CutToSizeLogic'
import MobileGrid from './MobileGrid'

type Props = {
  groups: CutToSizeGroupModel[]
  dimension: string
  thickness: string
  image: string | null
  updateButtonStatus: () => void
}

const CutToSizeGroupsModal = ({ groups, dimension, thickness, image, updateButtonStatus }: Props) => {
  const cutToSizeLogic: CutToSizeLogicModel = CutToSizeLogic({ groups, dimension, thickness })

  const [cutToSizeItems, setCutToSizeItems] = useState<CutToSizeItems[]>([])

  const columns = (): React.JSX.Element => {
    if (!cutToSizeLogic.hasProducts()) {
      let colSpan = cutToSizeLogic.constraints.length
      if (cutToSizeLogic.usesSize()) { colSpan += 1 }
      return (
        <td colSpan={colSpan} className='noAvailability-note'>This product is not available at the moment</td>
      )
    }
    return (
      <>
        { cutToSizeLogic.usesSize() &&
          <td className='inputColumn cuttingToolSize'>
            <DropDown values={cutToSizeLogic.sortArray(Object.keys(cutToSizeLogic.currentGroup.dimensions))} onValueChange={cutToSizeLogic.onDimensionChange} value={cutToSizeLogic.selectedDimension} />
            <span className='spacer'></span>
          </td>
        }
        {
          cutToSizeLogic.constraints.map((constraint) => {
            const isLength = constraint.field === cutToSizeLogic.LENGTH
            return (
              <td className='inputColumn sizeInput' key={`${cutToSizeLogic.selectedProduct?.name}-${constraint.field}-input`}>
                <SizeInput
                  sizeConstraint={constraint}
                  unit={isLength ? cutToSizeLogic.unitForLength : cutToSizeLogic.unitForWidth}
                  value={isLength ? cutToSizeLogic.length : cutToSizeLogic.width}
                  valid={cutToSizeLogic.calculateValidSize(isLength)}
                  min={isLength ? cutToSizeLogic.lengthMin : cutToSizeLogic.widthMin}
                  max={isLength ? cutToSizeLogic.lengthMax : cutToSizeLogic.widthMax}
                  step={isLength ? cutToSizeLogic.lengthStep : cutToSizeLogic.widthStep}
                  onSizeChange={cutToSizeLogic.onSizeChange}
                  onUnitChanged={cutToSizeLogic.onUnitChanged}
                />
              </td>
            )
          })
        }
      </>
    )
  }

  const submitOnCrossSellSummary = (): void => {
    if (cutToSizeLogic.isValid() && cutToSizeLogic.canSubmit) {
      setCutToSizeItems([
        ...cutToSizeItems, {
          product: cutToSizeLogic.selectedProduct.id,
          length: cutToSizeLogic.sizeInMM(cutToSizeLogic.length, cutToSizeLogic.unitForLength),
          width: cutToSizeLogic.sizeInMM(cutToSizeLogic.width, cutToSizeLogic.unitForWidth),
          dimension: cutToSizeLogic.selectedDimension,
          quantity: cutToSizeLogic.quantity,
          price: cutToSizeLogic.price.toFixed(2)
        }
      ])
      cutToSizeLogic.resetState()
    }
  }

  const cuttingToolQuantityChange = (value = 1): void => {
    cutToSizeLogic.setQuantity(cutToSizeLogic.quantity + value)
  }

  useEffect(updateButtonStatus, [cutToSizeItems])

  return (
    <div className='cuttingTool-table-container'
        data-product={cutToSizeLogic.selectedProduct ? cutToSizeLogic.selectedProduct.id : 0}
        data-length={cutToSizeLogic.sizeInMM(cutToSizeLogic.length, cutToSizeLogic.unitForLength)}
        data-width={cutToSizeLogic.sizeInMM(cutToSizeLogic.width, cutToSizeLogic.unitForWidth)}
        data-quantity={cutToSizeLogic.quantity}
        data-valid={cutToSizeLogic.isValid() ? 1 : 0}
        data-cansubmit={cutToSizeLogic.canSubmit}>
      <table className='customCutting-table product-table product-list-table'>
        <thead>
          <tr>
            <th className='productTable-sidePadding' style={{ width: '10.78px' }}></th>
            <th className='cuttingTool-tableHeader'></th>
            <th className='cuttingTool-tableHeader'></th>
            <th className='cuttingTool-tableHeader'></th>
            <th className='cuttingTool-tableHeader'></th>
            <th className='productTable-sidePadding' style={{ width: '10.78px' }}></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='productTable-sidePadding'>
              <img src={image} className='crossSellItem-summaryImage' alt="" width="78" height="72"/>
            </td>
            <td className='inputColumn'>
              { groups.length > 1
                ? (<DropDown values={groups.map((ctg) => ctg.name)} onValueChange={cutToSizeLogic.onGroupChange} value={cutToSizeLogic.currentGroup.name} />)
                : (<p className='groupName'>{cutToSizeLogic.currentGroup.name}</p>)
              }
            </td>
            {columns()}
            <td className='inputColumn quantityInputColumn'>
              <div className="cuttingTool-quantityContainer">
                <button
                  style={{ width: '35px' }}
                  type="button"
                  className="product-decrement-quantity basket-qty-change"
                  aria-label="decrease"
                  onClick={() => {
                    cuttingToolQuantityChange(-1)
                  }}
                  />
                <input
                  className="qty-input customCutting-field cuttingTool-quantityField"
                  min='0'
                  max='999'
                  value={cutToSizeLogic.quantity || ''}
                  autoComplete='off'
                  onChange={cutToSizeLogic.onQuantityChanged}
                  />
                <button
                  style={{ width: '30%' }}
                  type="button"
                  className="product-increment-quantity basket-qty-change"
                  aria-label="increase"
                  onClick={() => {
                    cuttingToolQuantityChange()
                  }}
                  />
              </div>
              <span className='crossSellModal-price'>£{(cutToSizeLogic.price).toFixed(2)}</span>
            </td>
            <td className='productTable-sidePadding'>
              <button className='crossSell-add' disabled={!(cutToSizeLogic.isValid() && cutToSizeLogic.canSubmit)} onClick={() => submitOnCrossSellSummary()}><span className="crossSell-addToSummary">Add</span></button>
            </td>
          </tr>
        </tbody>
      </table>
      <div className='customCutting-grid'>
        <div>
          <img src={image} className='crossSellItem-summaryImage' alt="" width="78" height="72"/>
        </div>

        {MobileGrid({ groups, cutToSizeLogic })}

        <div className={`cuttingTool-addToBasket ${cutToSizeLogic.isValid() && cutToSizeLogic.canSubmit ? 'active' : 'inactive'}`}>
          <button className='crossSell-add' disabled={!(cutToSizeLogic.isValid() && cutToSizeLogic.canSubmit)} onClick={() => submitOnCrossSellSummary()}><span className="crossSell-addToSummary">Add</span></button>
        </div>
      </div>

      <CutToSizeCrossSellSummary
        setCutToSizeItems={setCutToSizeItems}
        cutToSizeItems={[...cutToSizeItems].reverse()}
      />
    </div>
  )
}

export default CutToSizeGroupsModal
